/**
 * SetupGuide — Guía visual para conectar TikTok y Meta
 * Muestra los pasos exactos y URIs a copiar en cada plataforma
 */
import React, { useState } from 'react';
import { Copy, CheckCircle2, ExternalLink, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';

const PREVIEW_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api','') || window.location.origin;
const PROD_URL = 'https://www.neuralia369.com';

const TIKTOK_CB = `${PREVIEW_URL}/tiktok/oauth/callback`;
const META_CB   = `${PREVIEW_URL}/meta/oauth/callback`;
const TIKTOK_CB_PROD = `${PROD_URL}/tiktok/oauth/callback`;
const META_CB_PROD   = `${PROD_URL}/meta/oauth/callback`;

function CopyLine({ label, value, accent }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center gap-2 bg-slate-800/80 border border-slate-700 rounded-xl px-3 py-2">
      <div className="flex-1 min-w-0">
        {label && <div className="text-xs text-slate-500 mb-0.5">{label}</div>}
        <code className={`text-xs break-all ${accent || 'text-[#00d4ff]'}`}>{value}</code>
      </div>
      <button onClick={copy}
        className="flex-shrink-0 p-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors">
        {copied
          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          : <Copy className="w-3.5 h-3.5 text-slate-400" />}
      </button>
    </div>
  );
}

function Section({ title, color, step, children, link, linkLabel }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-5 hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
            style={{ background: color }}>
            {step}
          </div>
          <span className="font-bold text-white text-lg">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors hover:opacity-80"
              style={{ color, borderColor: `${color}40`, background: `${color}15` }}>
              <ExternalLink className="w-3.5 h-3.5" /> {linkLabel}
            </a>
          )}
          {open ? <ChevronDown className="w-5 h-5 text-slate-500" /> : <ChevronRight className="w-5 h-5 text-slate-500" />}
        </div>
      </button>
      {open && <div className="px-5 pb-5 space-y-3">{children}</div>}
    </div>
  );
}

function Step({ n, text, children }) {
  return (
    <div className="flex gap-3">
      <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs text-slate-400 flex-shrink-0 mt-0.5">
        {n}
      </div>
      <div className="flex-1 space-y-2">
        <p className="text-sm text-slate-300">{text}</p>
        {children}
      </div>
    </div>
  );
}

export default function SetupGuide() {
  return (
    <div className="space-y-6 max-w-3xl mx-auto">

      <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4">
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-amber-300 text-sm">Configuración pendiente en portales externos</p>
          <p className="text-slate-400 text-sm mt-1">
            El código está listo. Solo necesitas registrar las URLs de callback en TikTok y Meta.
            Sigue los pasos de abajo — copia y pega exactamente cada URL.
          </p>
        </div>
      </div>

      {/* ── TIKTOK ── */}
      <Section title="Conectar TikTok @neuralia3693" color="#fe2c55" step="A"
        link="https://developers.tiktok.com/apps" linkLabel="TikTok Developers">
        <Step n={1} text="Ve a developers.tiktok.com → Mi App → tu app con Client Key:">
          <CopyLine value="awmatfyl90ljzs48" label="Client Key de tu app" accent="text-[#fe2c55]" />
        </Step>
        <Step n={2} text="En tu app → Login Kit → Redirect URIs. Agrega AMBAS:">
          <CopyLine label="Preview (activo ahora)" value={TIKTOK_CB} />
          <CopyLine label="Producción (neuralia369.com)" value={TIKTOK_CB_PROD} />
        </Step>
        <Step n={3} text="En Scope → activa estos permisos:">
          <div className="flex flex-wrap gap-2">
            {['user.info.basic', 'video.publish', 'video.list'].map(s => (
              <code key={s} className="text-xs bg-slate-800 border border-slate-700 px-2 py-1 rounded-lg text-[#fe2c55]">{s}</code>
            ))}
          </div>
        </Step>
        <Step n={4} text="Guarda los cambios → vuelve aquí → pestaña TikTok → Conectar TikTok" />
        <Step n={5} text="Inicia sesión con la cuenta @neuralia3693 cuando TikTok te lo pida" />
      </Section>

      {/* ── META ── */}
      <Section title="Conectar Meta (Facebook + Instagram)" color="#1877F2" step="B"
        link="https://developers.facebook.com/apps" linkLabel="Meta Developers">
        <Step n={1} text="Ve a developers.facebook.com → tus Apps. Busca App ID:">
          <CopyLine value="1586191503184483" label="Meta App ID en el .env actual" accent="text-[#1877F2]" />
          <p className="text-xs text-amber-400 mt-1">
            ⚠️ Si no encuentras esta app, crea una nueva (tipo "Negocios") y actualiza META_APP_ID en el servidor.
          </p>
        </Step>
        <Step n={2} text="App → Configuración → Básica → Agregar Plataforma → Web:">
          <CopyLine label="URL del sitio (Preview)" value={PREVIEW_URL} />
          <CopyLine label="URL del sitio (Producción)" value={PROD_URL} />
        </Step>
        <Step n={3} text="Facebook Login → Configuración → URIs de redireccionamiento válidas:">
          <CopyLine label="Preview (activo ahora)" value={META_CB} />
          <CopyLine label="Producción" value={META_CB_PROD} />
        </Step>
        <Step n={4} text="Productos a activar en tu app:" >
          <div className="flex flex-wrap gap-2">
            {['Facebook Login', 'Instagram Graph API', 'Pages API'].map(p => (
              <span key={p} className="text-xs bg-slate-800 border border-slate-700 px-2 py-1 rounded-lg text-[#1877F2]">{p}</span>
            ))}
          </div>
        </Step>
        <Step n={5} text="Guarda → vuelve aquí → pestaña Conexiones → Conectar Meta" />
      </Section>

      {/* WhatsApp ya funciona */}
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        <div>
          <p className="font-semibold text-emerald-300 text-sm">WhatsApp Cloud API — Ya configurado</p>
          <p className="text-slate-400 text-sm">Token System User activo. Para enviar a números reales activa el modo LIVE en Meta Developer.</p>
        </div>
      </div>

    </div>
  );
}
